import { useUserIdentityContext } from "@core/services/user-identity.context";
import { getFormErrorMessage } from "@core/utility/get-error-message";
import { CouponFormRule, CouponFormSchema } from "@factory/form/form.rule";
import { useFormDefaultCoupon } from "@factory/form/form-default-coupon.hook";
import { ActionEnum } from "@factory/model/actions.enum";
import { GetResponseCouponModel } from "@factory/model/query-model-coupon";
import { 
  useQyDeleteCoupon, 
  useQyGetCouponById, 
  useQyUpdateCoupon} from "@factory/query/coupon.query";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { zodResolver } from "@hookform/resolvers/zod";
import { ButtonAction } from "@shared/ui/header-button/button-action";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import { confirmDialog } from "primereact/confirmdialog";
import { FieldErrors, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

export function useEditCoupon() {
  // LOCAL DECLARATION
  const { getUserRole } = useUserIdentityContext();
  const { formatDate } = useDateTimeContext();
  const { getDefault } = useFormDefaultCoupon();
  const navigate = useNavigate();
  const { mapUpdateCoupon, mapDeleteCoupon } = useFormApiMapper();
  const { showError, showSuccess } = useNotificationContext();
  const { couponId } = useParams();
  const actions: ButtonAction[] = [
    { label: "Update", type: "split", value: ActionEnum.Update },
    { label: "Delete", type: "more", value: ActionEnum.Delete },
  ];

  // QUERY DECLARATION
  // API GETBYID Coupon
  const handleGetApiSuccess = (data: GetResponseCouponModel) => {
    if (!data || data.data?.length === 0) {
      return;
    }

    const responseData = data.data?.[0];

      setValue("tenant", responseData?.tenant || "");
      setValue("name", responseData?.name || "");
      setValue("description", responseData?.description || "");
      setValue("couponCode", responseData?.coupon_code || "");
      setValue("validUntil", responseData?.valid_until || "");
      setValue("level", responseData?.level || "");
      setValue("purpose", responseData?.purpose || "");
      setValue("valueType", responseData?.value_type || "");
      setValue("points", responseData?.points);
      setValue("stamps", responseData?.stamps);
      setValue("redeemLimit", responseData?.redeem_limit);
      setValue("isUserRedeemable", Boolean(responseData?.is_user_redeemable));
  };
  const {
    data: couponResponse,
    isLoading,
    isError: couponError,
  } = useQyGetCouponById(couponId || "", handleGetApiSuccess);
  const couponData = couponResponse?.data?.[0];

  // API UPDATE Coupon
  const handleUpdateApiSuccess = () => {
    showSuccess("Coupon updated successfully");
  };
  const { mutate: updateCoupon, isLoading: isEditLoading } = useQyUpdateCoupon(
    handleUpdateApiSuccess
  );

  // API DELETE Coupon
  const handleDeleteApiSuccess = () => {
    handleBack();
    showSuccess("Coupon deleted successfully");
  };
  const { mutate: deleteCoupon } = useQyDeleteCoupon(
    handleDeleteApiSuccess
  );

  // LOCAL FUNCTIONS
  const formMethod = useForm<CouponFormSchema>({
    defaultValues: getDefault(couponData),
    resolver: zodResolver(CouponFormRule),
  });
  const { handleSubmit, setValue, watch, getValues } = formMethod;
  const handleValidate = (form: CouponFormSchema) => {
    if (!couponData || !couponData.code) {
      throw new Error("No coupon data");
    }

    const { tenant } = getUserRole();
    const updatedForm = {
      ...form,
      tenant,
    };

    const formData = mapUpdateCoupon(updatedForm, couponData.code);
    updateCoupon(formData);
  };
  const handleValidateError = (err: FieldErrors<CouponFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };
  const handleBack = () => {
    navigate("../");
  };
  const accept = () => {
    if (!couponData || !couponData.code) {
      throw new Error("No coupon data found");
    }

    const form = mapDeleteCoupon(couponData.code);
    deleteCoupon(form);
  };
  const handleAction = (action: ButtonAction) => {
    switch (action.value) {
      case ActionEnum.Update:
        handleSubmit(handleValidate, handleValidateError)();
        break;
      
      case ActionEnum.Delete:
        confirmDialog({
          message: "Are you sure you want to delete this record?",
          header: "Delete Confirmation",
          icon: "pi pi-info-circle",
          defaultFocus: "reject",
          acceptClassName: "p-button-danger",
          accept,
        });
        break;
    }
  };

   return {
    couponId,
    couponData,
    isLoading,
    couponError,
    formMethod,
    actions,
    isEditLoading,
    handleSubmit,
    setValue,
    watch,
    getValues,
    handleBack,
    handleAction,
  };
}
